import React, { useContext } from 'react';
import { useUser } from 'store/User/Context';
import styled from 'styled-components';
import { ProductPurchaseContext } from 'utils/context/ProductPurchaseContext';
import { formatNumber } from 'utils/formatters/formatNumber';

import Button from 'components/atom/Button';
import Indicator from 'components/atom/Indicator';
import { IconEnum } from 'components/common/Icon';
import ProductPurchaseForm from 'components/common/Purchase/ProductPurchaseForm';
import PurchaseStatusInfo from 'components/common/Purchase/PurchaseStatusInfo';

const PageContainer = styled.div<{ isBackgroundTransparent?: boolean }>`
    display: flex;
    width: 98%;
    height: auto;
    padding: 20px;
    margin: 20px;
    border-radius: 10px;
    background: ${(props) => props.isBackgroundTransparent ? 'transparent' : 'var(--color-white)'};
    
    @media (max-width: 992px) {
        flex-direction: column;
        padding: 10px;
        margin: 10px;
    }
`;

const EmptyCartContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 90%;
    gap: 30px;
`;

const FormSection = styled.div`
    width: 66%;
    padding-right: 20px;
    
    @media (max-width: 992px) {
        width: 100%;
        padding-right: 0;
    }
`;

const OrderSummarySection = styled.div`
    width: 33%;
    border-left: 1px solid var(--color-alt-second-10);
    padding-left: 20px;

    @media (max-width: 992px) {
        width: 100%;
        margin-top: 30px;
        border-left: none;
        padding-left: 0;
    }
`;

const OrderSummaryCard = styled.div`
    padding: 25px;
`;

const OrderSummaryTitle = styled.h3`
    font-size: 18px;
    margin-bottom: 20px;
    color: var(--color-primary);
    font-weight: 600;
`;

const OrderSummaryItem = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 15px;
    font-size: 14px;
`;

const OrderSummaryTotal = styled.div`
    display: flex;
    justify-content: space-between;
    margin-top: 20px;
    padding-top: 15px;
    border-top: 1px solid #eee;
    font-weight: 600;
    font-size: 16px;
`;

const SectionTitle = styled.h2`
    font-size: 22px;
    margin-bottom: 20px;
    color: var(--color-primary);
`;

const ButtonContainer = styled.div`
    width: 100%;
    margin-top: 185px;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    
    @media (max-width: 768px) {
        margin-top: 90px;
    }
`;

const StyledButton = styled(Button)`
    width: 100%;
    max-width: 400px;
`;

const QuantityContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const LoadingOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.9);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
`;

const LoadingContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
`;

const LoadingText = styled.div`
    padding-bottom: 100px;
    font-size: 16px;
    color: var(--color-primary);
    text-align: center;
    z-index: 999999;
`;

interface QuantityButtonProps {
    disabled?: boolean;
}

const QuantityButton = styled.div<QuantityButtonProps>`
    background: var(--color-alt-second);
    border: 1px solid var(--color-alt-second-30);
    border-radius: 4px;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: var(--color-primary);
    
    &:hover {
        background: var(--color-alt-second-20);
    }
    
    ${({ disabled }) => disabled && `
        opacity: 0.5;
        cursor: not-allowed;
        pointer-events: none;
        
        &:hover {
            background: var(--color-alt-second);
        }
    `}
    
    img {
        width: 12px;
        height: 12px;
    }
`;

const PurchaseProductPage = () => {
    const { user } = useUser();
    const {
        purchaseState,
        handleInvoiceDataChange,
        handleQuantityChange,
        handleSubmitForm,
        handleCancel,
        getDisplayValue,
        canIncrease,
        canDecrease
    } = useContext(ProductPurchaseContext);

    if (purchaseState.isInitializing) {
        return <Indicator />;
    }

    if (purchaseState.showPurchaseStatus) {
        return (
            <PageContainer isBackgroundTransparent={true}>
                <PurchaseStatusInfo
                    key={`${purchaseState.status}-${purchaseState.processStep}-${purchaseState.isLoading}`}
                    purchaseType={purchaseState.productType}
                    status={purchaseState.status}
                    processStep={purchaseState.processStep}
                    orderData={purchaseState.currentOrder ? purchaseState.currentOrder : undefined}
                    isNewAccount={false}
                    isLoading={purchaseState.isLoading}
                    isPolling={purchaseState.isPolling}
                    onCancel={handleCancel}
                />
            </PageContainer>
        );
    }

    if (!purchaseState.cart || purchaseState.cart.items.length === 0) {
        return (
            <PageContainer>
                <EmptyCartContainer>
                    <h2>Koszyk jest pusty</h2>
                    <p>Nie udało się dodać produktu do koszyka.</p>
                    <Button onClick={handleCancel}>
                        Wróć do ustawień
                    </Button>
                </EmptyCartContainer>
                {(purchaseState.isLoading || purchaseState.isPolling) &&
                    <LoadingOverlay>
                        <LoadingContent>
                            <Indicator />
                            {purchaseState.processStep && <LoadingText>{purchaseState.processStep}</LoadingText>}
                        </LoadingContent>
                    </LoadingOverlay>
                }
            </PageContainer>
        );
    }

    const totalPriceInZloty = purchaseState.cart.totalPrice / 100;

    return (
        <PageContainer>
            <FormSection>
                <ProductPurchaseForm
                    isCreateAccount={false}
                    isDarkMode={user.isDarkMode}
                    accountData={undefined}
                    invoiceData={purchaseState.invoiceData}
                    errors={purchaseState.errors}
                    triggerValidation={purchaseState.triggerValidation}
                    onAccountDataChange={undefined}
                    onInvoiceDataChange={handleInvoiceDataChange}
                    onSubmit={handleSubmitForm}
                />
            </FormSection>

            <OrderSummarySection>
                <SectionTitle>Szczegóły zamówienia</SectionTitle>
                <OrderSummaryCard>
                    <OrderSummaryTitle>Zawartość koszyka</OrderSummaryTitle>

                    {purchaseState.cart.items.map((item) => {
                        const displayValue = getDisplayValue(item);

                        return (
                            <React.Fragment key={item.productId}>
                                <OrderSummaryItem>
                                    <span>{item.name}</span>
                                    <span>
                                        <QuantityContainer>
                                            <QuantityButton
                                                onClick={() => handleQuantityChange(item.productId, false)}
                                                disabled={!canDecrease(item) || purchaseState.isLoadingCart}
                                            >
                                                -
                                            </QuantityButton>
                                            {formatNumber(displayValue)}
                                            <QuantityButton
                                                onClick={() => handleQuantityChange(item.productId, true)}
                                                disabled={!canIncrease(item) || purchaseState.isLoadingCart}
                                            >
                                                +
                                            </QuantityButton>
                                        </QuantityContainer>
                                    </span>
                                </OrderSummaryItem>
                            </React.Fragment>
                        );
                    })}

                    <OrderSummaryTotal>
                        <span><strong>Kwota łącznie:</strong></span>
                        <span><strong>{totalPriceInZloty.toFixed(2)} zł</strong></span>
                    </OrderSummaryTotal>

                    <ButtonContainer>
                        <StyledButton
                            onClick={handleSubmitForm}
                            actionIcon={IconEnum.SEND}
                            disabled={
                                purchaseState.isLoading ||
                                purchaseState.isPolling ||
                                !purchaseState.cart?.items.length ||
                                purchaseState.isLoadingCart
                            }
                        >
                            {purchaseState.isLoading
                                ? 'Przetwarzanie...'
                                : purchaseState.isPolling
                                    ? 'Oczekiwanie na płatność...'
                                    : purchaseState.isLoadingCart
                                        ? 'Aktualizowanie...'
                                        : 'Dalej'}
                        </StyledButton>

                        <StyledButton
                            onClick={handleCancel}
                            actionIcon={IconEnum.CROSS}
                            disabled={purchaseState.isLoading && !purchaseState.isPolling}
                            important
                        >
                            Anuluj
                        </StyledButton>
                    </ButtonContainer>
                </OrderSummaryCard>
            </OrderSummarySection>

            {(purchaseState.isLoading || purchaseState.isPolling) &&
                <LoadingOverlay>
                    <LoadingContent>
                        <Indicator />
                        {purchaseState.processStep && <LoadingText>{purchaseState.processStep}</LoadingText>}
                    </LoadingContent>
                </LoadingOverlay>
            }
        </PageContainer>
    );
};

export default PurchaseProductPage;
